import { LinearProgress, useTheme, Box } from "@mui/material";
import { FileIcon } from "~components/Icons";
import { getStyles } from "../styles";

interface ProgressDetails {
  status: string;
  totalPages: number;
  pagesCompleted: number;
  referencesSearched: number;
  referencesFound: number;
  totalProducts: number;
  productsSaved: number;
  progressPercent: number;
  categoriesCount?: number;
  rowsProcessed?: number;
  productsProcessed?: number;
  processingMessage?: string;
}

interface Props {
  isCompleted: boolean;
  filename: string;
  message: string;
  progress: number;
  progressDetails?: ProgressDetails;
}

const Loading = ({
  isCompleted,
  filename,
  message,
  progress,
  progressDetails,
}: Props) => {
  const theme = useTheme();
  const { styles } = getStyles(theme, isCompleted);

  const getStageInfo = () => {
    // Se não há progressDetails ainda, não mostrar bloco extra de estágio.
    // Nessa fase usamos apenas a mensagem principal (prop message).
    if (!progressDetails || !progressDetails.status) {
      return null;
    }

    const {
      status,
      totalPages,
      pagesCompleted,
      referencesFound,
      referencesSearched,
      totalProducts,
      productsSaved,
      rowsProcessed,
      productsProcessed,
      processingMessage,
      progressPercent,
    } = progressDetails;

    // Usar processingMessage se disponível (mais detalhado)
    if (processingMessage) {
      // Extrair dados do processFile se disponível (resumo da leitura)
      const processFileMatch = processingMessage.match(
        /processFile - Success === \{([^}]+)\}/
      );
      const categoriesMatch = processingMessage.match(/categoriesCount: (\d+)/);
      const productsMatch = processingMessage.match(/productsCount: (\d+)/);
      const invalidMatch = processingMessage.match(/invalidProductsCount: (\d+)/);
      const rowsMatch = processingMessage.match(/rowsProcessed: (\d+)/);

      const msgLower = processingMessage.toLowerCase();
      const isPreparingStructure = msgLower.includes(
        "preparando estrutura do banco de dados"
      );
      const isCategoriesPrepared = msgLower.includes("categorias preparadas");

      // PASSO 1 – leitura/validação da planilha
      if (
        status === "processing" &&
        (processFileMatch || (!isPreparingStructure && !isCategoriesPrepared))
      ) {
        return (
          <Box sx={{ mt: 0.5 }}>
            <Box
              sx={{
                fontSize: "10px",
                color: theme.palette.grey[400],
                lineHeight: 1.3,
                fontWeight: 500,
              }}
            >
              Passo 1: Lendo e validando planilha
            </Box>
            {processFileMatch && (
              <Box
                sx={{
                  fontSize: "9px",
                  color: theme.palette.grey[400],
                  lineHeight: 1.4,
                  mt: 0.5,
                  fontFamily: "monospace",
                }}
              >
                <Box component="span" sx={{ display: "block" }}>
                  processFile - Success === {"{"}
                </Box>
                {categoriesMatch && (
                  <Box component="span" sx={{ display: "block", ml: 1 }}>
                    categoriesCount: {categoriesMatch[1]},
                  </Box>
                )}
                {productsMatch && (
                  <Box component="span" sx={{ display: "block", ml: 1 }}>
                    productsCount: {productsMatch[1]},
                  </Box>
                )}
                {invalidMatch && (
                  <Box component="span" sx={{ display: "block", ml: 1 }}>
                    invalidProductsCount: {invalidMatch[1]},
                  </Box>
                )}
                {rowsMatch && (
                  <Box component="span" sx={{ display: "block", ml: 1 }}>
                    rowsProcessed: {rowsMatch[1]}
                  </Box>
                )}
                <Box component="span" sx={{ display: "block" }}>
                  {"}"}
                </Box>
              </Box>
            )}
          </Box>
        );
      }

      // PASSO 2 – preparando estrutura de banco/categorias
      if (status === "processing" && (isPreparingStructure || isCategoriesPrepared)) {
        return (
          <Box sx={{ mt: 0.5 }}>
            <Box
              sx={{
                fontSize: "10px",
                color: theme.palette.grey[400],
                lineHeight: 1.3,
                fontWeight: 500,
              }}
            >
              Passo 2: Preparando estrutura do banco de dados
            </Box>
            <Box
              sx={{
                fontSize: "9px",
                color: theme.palette.grey[400],
                lineHeight: 1.2,
                mt: 0.5,
                fontWeight: isCategoriesPrepared ? 500 : 400,
              }}
            >
              {isCategoriesPrepared ? "Categorias preparadas" : processingMessage}
            </Box>
          </Box>
        );
      }

      // PASSO 3 – inserção no banco (syncing)
      if (status === "syncing") {
        return (
          <Box sx={{ mt: 0.5 }}>
            <Box
              sx={{
                fontSize: "10px",
                color: theme.palette.grey[400],
                lineHeight: 1.3,
                fontWeight: 500,
              }}
            >
              Passo 3: Salvando produtos no banco de dados
            </Box>
            <Box
              sx={{
                fontSize: "9px",
                color: theme.palette.grey[400],
                lineHeight: 1.2,
                mt: 0.5,
              }}
            >
              {processingMessage}
            </Box>
            {totalProducts && productsSaved !== undefined && (
              <Box
                sx={{
                  fontSize: "9px",
                  color: theme.palette.grey[400],
                  lineHeight: 1.2,
                  mt: 0.25,
                  fontWeight: 500,
                }}
              >
                {productsSaved} de {totalProducts} produtos salvos ({progressPercent || 0}
                %)
              </Box>
            )}
          </Box>
        );
      }
    }

    // Fallback para lógica antiga (compatibilidade)
    if (status === "processing") {
      return (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ fontSize: "9px", color: theme.palette.grey[400], lineHeight: 1.2 }}>
            Etapa 1/2: Processando planilha • {rowsProcessed || 0} linhas processadas •{" "}
            {productsProcessed || 0} produtos encontrados
          </Box>
        </Box>
      );
    } else if (status === "syncing") {
      return (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ fontSize: "9px", color: theme.palette.grey[400], lineHeight: 1.2 }}>
            Etapa 2/2: Salvando no banco de dados • {productsSaved || 0} de{" "}
            {totalProducts || 0} produtos salvos
          </Box>
        </Box>
      );
    } else if (totalPages > 0) {
      const foundCount = referencesFound || 0;
      const searchedCount = referencesSearched || 0;
      const unregisteredTotal = Math.max(0, searchedCount - foundCount);

      return (
        <Box sx={{ mt: 0.5 }}>
          <Box
            sx={{
              fontSize: "9px",
              color: theme.palette.grey[400],
              lineHeight: 1.2,
              mb: 0.25,
            }}
          >
            Etapa 1/2: Processando planilha • Página {pagesCompleted} de {totalPages}
          </Box>
          {(foundCount > 0 || unregisteredTotal > 0) && (
            <Box sx={{ fontSize: "9px", lineHeight: 1.2 }}>
              {foundCount > 0 && (
                <Box component="span" sx={{ color: theme.palette.primary.light, mr: 1 }}>
                  ✓ {foundCount} produtos processados
                </Box>
              )}
              {unregisteredTotal > 0 && (
                <Box component="span" sx={{ color: theme.palette.error.main }}>
                  ✗ {unregisteredTotal} produtos inválidos
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <div style={styles.wrapper}>
      <FileIcon />
      <div style={styles.content}>
        <p style={styles.title}>{filename}</p>
        <LinearProgress variant="determinate" value={progress} sx={styles.loader} />
        {/* 
          Quando já temos progressDetails/status do backend, a área de estágios
          (getStageInfo) mostra as mensagens detalhadas. Para evitar textos
          duplicados, só exibimos a mensagem principal enquanto ainda não há
          status estruturado.
        */}
        {(!progressDetails || !progressDetails.status) && (
          <p style={styles.text}>{message}</p>
        )}
        {getStageInfo()}
      </div>
    </div>
  );
};

export default Loading;
